/**
 * CompositeProductPreprocessor - Product Virtualization Utility (Phase 1 Bootstrap)
 * ⚡ PPD-FIRST ARCHITECTURE - Bootstrap-Only Utility Module
 *
 * ARCHITECTURAL ROLE:
 * - Phase 1 Bootstrap utility called ONLY by data-pipeline.js Layer 2
 * - Groups individual products into CompositeProducts for simplified UI display
 * - NOT a Phase 2 event-driven module (no business logic, no runtime calls)
 * - ZERO runtime usage allowed - bootstrap-protected with enforced errors
 *
 * EVENT-DRIVEN ARCHITECTURE:
 * - Publishes comprehensive event lifecycle during bootstrap processing
 * - Performance tracking with 50ms threshold for product grouping operations
 * - Event-driven status reporting for pipeline coordination
 *
 * @class CompositeProductPreprocessor
 * @version 2.0.0
 * @date 2026-02-03
 */

// ============================================================================
// EVENT CONSTANTS
// ============================================================================

// ✅ MODERNIZED: Use centralized CONSTANTS.EVENTS
const _CPP_CONSTANTS = window.MurphyDoorConfigurator?.CONSTANTS;
const EVENT_TYPES = _CPP_CONSTANTS
  ? {
      OPERATION_STARTED:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_STARTED,
      OPERATION_COMPLETED:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_COMPLETED,
      OPERATION_FAILED:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_FAILED,
      PERFORMANCE_SLOW:
        _CPP_CONSTANTS.EVENTS.PERFORMANCE_COMPOSITE_PREPROCESSING_SLOW,
      GROUPING_COMPLETED:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_GROUPING_COMPLETED,
      COMPOSITE_CREATED: _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PRODUCT_CREATED,
      EMPTY_INPUT:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_EMPTY_INPUT,
      FALLBACK_ACTIVATED:
        _CPP_CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_FALLBACK,
    }
  : {
      // Fallback: raw strings if CONSTANTS not yet available (CONSTANTS.EVENTS equivalents)
      OPERATION_STARTED: 'data:composite_preprocessing_started', // CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_STARTED
      OPERATION_COMPLETED: 'data:composite_preprocessing_completed', // CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_COMPLETED
      OPERATION_FAILED: 'data:composite_preprocessing_failed', // CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_FAILED
      PERFORMANCE_SLOW: 'performance:composite_preprocessing_slow', // CONSTANTS.EVENTS.PERFORMANCE_COMPOSITE_PREPROCESSING_SLOW
      GROUPING_COMPLETED: 'data:composite_grouping_completed', // CONSTANTS.EVENTS.DATA_COMPOSITE_GROUPING_COMPLETED
      COMPOSITE_CREATED: 'data:composite_product_created', // CONSTANTS.EVENTS.DATA_COMPOSITE_PRODUCT_CREATED
      EMPTY_INPUT: 'data:composite_preprocessing_empty_input', // CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_EMPTY_INPUT
      FALLBACK_ACTIVATED: 'data:composite_preprocessing_fallback', // CONSTANTS.EVENTS.DATA_COMPOSITE_PREPROCESSING_FALLBACK
    };

const PERFORMANCE_THRESHOLD = 50; // ms - Product grouping should be fast

// Dev-only logging (production guard)
const isDev = () =>
  window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

// ============================================================================
// CLASS DEFINITION
// ============================================================================

class CompositeProductPreprocessor {
  constructor() {
    this.initialized = false;
    this.bootstrapComplete = false; // Track bootstrap completion for runtime protection
    this.variantIdCounter = 0; // Counter for generating unique variant IDs
    this.processingStats = {
      totalProcessed: 0,
      groupsCreated: 0,
      averageProcessingTime: 0,
    };
    this.callCount = 0; // Track number of process() calls
  }

  /**
   * Main processing method - transforms raw products into displayable products
   * ⚡ BOOTSTRAP-ONLY METHOD - Throws error if called after bootstrap complete
   *
   * @param {Array} rawProducts - Array of raw product objects
   * @returns {Array} Array of Product | CompositeProduct objects for display
   */
  process(rawProducts) {
    // ============================================================================
    // CRITICAL: BOOTSTRAP-ONLY RUNTIME PROTECTION
    // ============================================================================
    if (this.bootstrapComplete) {
      const error =
        '❌ CRITICAL ARCHITECTURAL VIOLATION: CompositeProductPreprocessor.process() called AFTER bootstrap complete! ' +
        'This is a Phase 1 Bootstrap utility that MUST ONLY be called during data-pipeline.js Layer 2 processing. ' +
        'Runtime Phase 2 modules MUST use pre-built CompositeProducts from PPD (conductor.getProduct(id)) instead. ' +
        'This violation indicates a Phase 2 module is trying to build CompositeProducts at runtime, which violates PPD-First architecture. ' +
        'Stack trace:';
      isDev() && console.error(error, new Error().stack);
      throw new Error(error);
    }

    isDev() &&
      console.log(
        '🚀 [CompositeProductPreprocessor] Phase 1 execution: Product virtualization started'
      );
    const startTime = window.performance.now();
    const METHOD_SIGNATURE = 'CompositeProductPreprocessor.process';
    this.callCount++;

    // Use verbose logging for first few calls, then compact logging
    const isVerbose = this.callCount <= 3;

    try {
      // ============================================================================
      // STAGE 1: OPERATION STARTED
      // ============================================================================
      this.publishEvent(EVENT_TYPES.OPERATION_STARTED, {
        productCount: rawProducts?.length || 0,
        callCount: this.callCount,
        isVerbose,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      if (isVerbose) {
        // CCP Delegation Confirmation
        isDev() &&
          console.log(
            '🎯 CompositeProduct processing initiated - CCP compliant'
          );
      }

      // ============================================================================
      // EDGE CASE: Empty Input
      // ============================================================================
      if (!rawProducts || rawProducts.length === 0) {
        this.publishEvent(EVENT_TYPES.EMPTY_INPUT, {
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        if (isVerbose) {
          isDev() &&
            console.log(
              '⚠️ EDGE CASE: No products to virtualize, returning empty array'
            );
        }

        // Even empty operations need completion event
        this.publishEvent(EVENT_TYPES.OPERATION_COMPLETED, {
          productCount: 0,
          groupCount: 0,
          compositeCount: 0,
          executionTime: '0.00',
          timestamp: Date.now(),
          source: METHOD_SIGNATURE,
        });

        return [];
      }

      // ============================================================================
      // STAGE 2: BUSINESS LOGIC - Product Grouping
      // ============================================================================

      // Group products by display name patterns
      const grouped = this.groupByDisplayName(rawProducts);

      // Log grouping results for performance tracking
      const compositeCount = Object.values(grouped).filter(
        (g) => g.products.length > 1
      ).length;

      // Publish grouping milestone event
      this.publishEvent(EVENT_TYPES.GROUPING_COMPLETED, {
        inputProductCount: rawProducts.length,
        groupCount: Object.keys(grouped).length,
        compositeCount,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      if (isVerbose) {
        isDev() &&
          console.log(
            `📊 Grouped ${rawProducts.length} products → ${Object.keys(grouped).length} groups (${compositeCount} CompositeProducts)`
          );
      } else if (compositeCount > 0) {
        // Compact logging: only show when CompositeProducts are actually created
        isDev() &&
          console.log(
            `🎯 CompositeProduct: ${rawProducts.length}→${Object.keys(grouped).length} (${compositeCount} composite)`
          );
      }

      // Create displayable products (CompositeProducts for groups, regular Products for singles)
      const displayableProducts = this.createDisplayableProducts(grouped);

      // Update processing statistics
      const processingTime = window.performance.now() - startTime;
      this.updateProcessingStats(
        rawProducts.length,
        Object.keys(grouped).length,
        processingTime
      );

      // ============================================================================
      // STAGE 3: PERFORMANCE TRACKING
      // ============================================================================
      const executionTime = window.performance.now() - startTime;

      if (executionTime > PERFORMANCE_THRESHOLD) {
        this.publishEvent(EVENT_TYPES.PERFORMANCE_SLOW, {
          executionTime: executionTime.toFixed(2),
          threshold: PERFORMANCE_THRESHOLD,
          productCount: rawProducts.length,
          groupCount: Object.keys(grouped).length,
          source: METHOD_SIGNATURE,
          timestamp: Date.now(),
        });

        isDev() &&
          console.warn(
            `⚠️ PERFORMANCE: CompositeProduct processing took ${executionTime.toFixed(2)}ms ` +
              `(threshold: ${PERFORMANCE_THRESHOLD}ms, products: ${rawProducts.length})`
          );
      }

      // ============================================================================
      // STAGE 4: OPERATION COMPLETED
      // ============================================================================

      // Performance & completion logging for P3.3.1 optimization
      const finalCompositeCount = displayableProducts.filter(
        (p) => p.isComposite
      ).length;

      this.publishEvent(EVENT_TYPES.OPERATION_COMPLETED, {
        productCount: displayableProducts.length,
        compositeCount: finalCompositeCount,
        executionTime: executionTime.toFixed(2),
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      if (isVerbose) {
        isDev() &&
          console.log(
            `✅ Virtualization complete: ${displayableProducts.length} products (${finalCompositeCount} CompositeProducts) in ${Math.round(processingTime)}ms`
          );
      }

      return displayableProducts;
    } catch (error) {
      // ============================================================================
      // STAGE 5: OPERATION FAILED
      // ============================================================================
      this.publishEvent(EVENT_TYPES.OPERATION_FAILED, {
        error: error.message,
        stack: error.stack,
        productCount: rawProducts?.length || 0,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      isDev() && console.error('❌ Product virtualization failed:', error);

      // Publish fallback activation event
      this.publishEvent(EVENT_TYPES.FALLBACK_ACTIVATED, {
        reason: 'Processing error',
        error: error.message,
        timestamp: Date.now(),
        source: METHOD_SIGNATURE,
      });

      // Fallback: return original products
      return rawProducts;
    }
  }

  /**
   * Groups products by display name using intelligent pattern extraction
   * @param {Array} rawProducts - Raw product array
   * @returns {Object} Grouped products by display name
   */
  groupByDisplayName(rawProducts) {
    const groups = {};

    rawProducts.forEach((product) => {
      // Extract and normalize display name (defensive trim to handle any edge cases)
      const displayName = (this.extractDisplayName(product) || '').trim();

      // CRITICAL FIX: Extract category BEFORE grouping to prevent collisions
      // Issue: Products like "Standard Shelves" appear in both Top Pocket and Bottom Pocket categories
      // Without category context in the grouping key, last processed overwrites first
      const rawCategory =
        product.configurator_tab_category?.category ||
        product.category ||
        'Unknown';

      // WHITESPACE FIX: Trim category to handle human typos in product setup
      // Example: 'Front Casing ' or ' Front Casing' → 'Front Casing'
      const category = rawCategory.trim();

      // CRITICAL FIX: Use displayName + category to prevent collisions between categories
      // Example: "Standard Shelves__Top Pocket" vs "Standard Shelves__Bottom Pocket"
      const groupKey = `${displayName}__${category}`;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          displayName: displayName,
          products: [],
          metadata: {
            category: category,
            basePrice: product.price || 0,
            availability: product.availability || 'available',
          },
        };
      }

      groups[groupKey].products.push(product);
    });

    // Grouping moved to main process method for cleaner flow

    return groups;
  }

  /**
   * Extracts clean display name from product using pattern matching
   * Handles patterns like "Product - Variant" and "Product (Variant)"
   * @param {Object} product - Product object
   * @returns {string} Clean display name
   */
  extractDisplayName(product) {
    let title = product.title || product.name || 'Unnamed Product';
    const originalTitle = title;

    // Phase 1: Remove common variant suffixes
    // Pattern: "Product Name - Variant" -> "Product Name"
    title = title.replace(/\s*-\s*[^-]+$/, '');

    // Pattern: "Product Name (Variant)" -> "Product Name"
    title = title.replace(/\s*\([^)]+\)$/, '');

    // Pattern: "Product Name | Variant" -> "Product Name"
    title = title.replace(/\s*\|\s*[^|]+$/, '');

    // Phase 2: Remove material/finish descriptors (common in furniture/doors)
    // Remove wood types and finishes from the end - but preserve complete parenthetical expressions
    title = title.replace(
      /\s+\((white|paint grade|veneers|cherry|oak|maple|pine|walnut|mahogany|natural|stained|finished|black|brown|espresso|honey)[^)]*\)/gi,
      ''
    );
    title = title.replace(
      /\s+(in|with|cherry|oak|maple|pine|walnut|mahogany|natural|stained|finished|white|black|brown|espresso|honey).*$/i,
      ''
    );

    // Phase 3: Category-specific grouping patterns
    const lowerTitle = title.toLowerCase();

    // Front Casing / Door patterns
    if (
      lowerTitle.includes('door') ||
      lowerTitle.includes('bookcase') ||
      lowerTitle.includes('casing')
    ) {
      if (lowerTitle.includes('raised') && lowerTitle.includes('panel')) {
        return 'Raised Panel Style';
      } else if (lowerTitle.includes('flat') && lowerTitle.includes('panel')) {
        return 'Flat Panel Style';
      } else if (lowerTitle.includes('shaker')) {
        return 'Shaker Style';
      } else if (lowerTitle.includes('mission')) {
        return 'Mission Style';
      } else if (lowerTitle.includes('traditional')) {
        return 'Traditional Style';
      } else if (
        lowerTitle.includes('contemporary') ||
        lowerTitle.includes('modern')
      ) {
        return 'Contemporary Style';
      }
    }

    // Phase 4: Generic fallback - extract base product name
    // Remove size descriptors, colors, and numbers from the end
    title = title.replace(/\s+\d+[^a-zA-Z]*$/, ''); // Remove trailing numbers/sizes
    title = title.replace(/\s+(small|medium|large|xl|xxl)$/i, ''); // Remove size descriptors

    // Phase 5: FIXED - Special handling for important product names
    // Preserve full "Standard Front Entablature" for proper CompositeProduct grouping
    const words = title.trim().split(/\s+/);

    // Preserve important product names for CompositeProduct grouping
    if (title.toLowerCase().includes('standard front entablature')) {
      return title.trim();
    }

    // CRITICAL FIX: Shelf pattern handling — covers both the legacy Top/Bottom
    // Pocket products ("Standard Top Pocket Shelves" → "Standard Shelves") and
    // the merged "Shelf Style" hybrid products ("Standard Shelves (TFL)" →
    // "Standard Shelves"). Deliberately does NOT require "pocket" in the title:
    // the hybrid naming convention drops that word, and species words like
    // "TFL" aren't in the Phase 2 stripping list above, so without this branch
    // the TFL variant would keep "(TFL)" in its display name and fail to group
    // with the other 3 species under the same composite.
    if (lowerTitle.includes('shelves')) {
      if (lowerTitle.includes('standard')) {
        return 'Standard Shelves';
      } else if (lowerTitle.includes('lipped')) {
        return 'Lipped Shelves';
      }
    }

    // For other long titles, group by first 2-3 words
    if (words.length > 3) {
      return words.slice(0, 2).join(' ');
    }

    // Clean up and return
    title = title.trim();
    return title || originalTitle || 'Unnamed Product';
  }

  /**
   * Creates displayable products from grouped data
   * @param {Object} grouped - Grouped products by display name
   * @returns {Array} Array of Product | CompositeProduct objects
   */
  createDisplayableProducts(grouped) {
    const displayableProducts = [];

    Object.values(grouped).forEach((group) => {
      if (group.products.length === 1) {
        // Single product - return as-is
        displayableProducts.push(group.products[0]);
      } else {
        // Multiple products - create CompositeProduct
        const compositeProduct = this.createCompositeProduct(group);
        displayableProducts.push(compositeProduct);

        // Publish composite creation event
        this.publishEvent(EVENT_TYPES.COMPOSITE_CREATED, {
          compositeId: compositeProduct.id,
          displayName: compositeProduct.display_name,
          productCount: group.products.length,
          category: compositeProduct.category,
          timestamp: Date.now(),
        });
      }
    });

    return displayableProducts;
  }

  /**
   * Creates a CompositeProduct from a product group
   * @param {Object} group - Product group with display name and products
   * @returns {Object} CompositeProduct object
   */
  createCompositeProduct(group) {
    const primaryProduct = group.products[0]; // Use first product as template

    // CompositeProduct creation logging (reduced verbosity)
    // Note: Summary already provided in processProductsToGroups grouping results

    return {
      // CompositeProduct identification
      isComposite: true,
      compositeGroupName: group.displayName,
      compositeProductCount: group.products.length,

      // Primary product data (for compatibility)
      id: `composite-${this.generateGroupId(group.displayName, group.metadata.category)}`,
      title: group.displayName,
      display_name: group.displayName, // Add for UI compatibility
      'data-group-id': `group_${group.metadata.category.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}-${group.displayName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`, // Add for navigation manager DOM attributes
      handle: primaryProduct.handle || this.slugify(group.displayName),

      // Pricing (use primary product or calculate range)
      price: primaryProduct.price || 0,
      compareAtPrice: primaryProduct.compareAtPrice || 0,
      priceRange: this.calculatePriceRange(group.products),

      // Variants for UI compatibility - FIXED: Extract and merge ALL variants from constituent products
      variants: this.extractAllVariants(group.products), // All individual variants from all grouped products

      // Image (use primary product image)
      image: primaryProduct.image || primaryProduct.featured_image,
      featured_image: primaryProduct.image || primaryProduct.featured_image,

      // Configurator Images (preserve front/back images for configurator display)
      configuratorFrontImage: primaryProduct.configurator_front_image || '',
      configuratorBackImage: primaryProduct.configurator_back_image || '',

      // Essential product properties (CRITICAL: preserve for shipping calc & variant enhancement)
      woods: primaryProduct.woods || [],
      production_time: primaryProduct.production_time || {
        low: null,
        high: null,
      },

      // Availability
      availability: this.calculateAvailability(group.products),
      availableCount: group.products.filter(
        (p) => p.availability === 'available'
      ).length,
      images: group.products
        .map((p) => p.image || p.featured_image)
        .filter((img) => img),

      // Metadata
      category: group.metadata.category,
      configurator_tab_category: primaryProduct.configurator_tab_category || {
        category: group.metadata.category,
      },
      tags: this.consolidateTags(group.products),
      vendor: primaryProduct.vendor || 'Murphy Door',
      productType: primaryProduct.product_type || 'Door Component',

      // CompositeProduct-specific data
      sourceProducts: group.products,
      variantCount: group.products.reduce(
        (total, p) => total + (p.variants?.length || 1),
        0
      ),

      // Material compatibility (for smart sorting)
      materials: this.extractMaterials(group.products),
      finishes: this.extractFinishes(group.products),

      // Display attributes for E2E testing
      'data-is-composite': 'true',
      'data-group-id': `group_${this.generateGroupId(group.displayName, group.metadata.category)}`,
      'data-composite-group-name': group.displayName,
      'data-composite-product-count': group.products.length.toString(),
    };
  }

  /**
   * Generates consistent group ID for CompositeProduct with category context
   * @param {string} displayName - Display name
   * @param {string} category - Category context to prevent collisions between categories
   * @returns {string} Group ID
   */
  generateGroupId(displayName, category = '') {
    // Include category prefix to prevent collisions like "Standard Shelves" in Top Pocket vs Bottom Pocket
    const categoryPrefix = category ? `${this.slugify(category)}-` : '';
    const baseId = displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();

    return `${categoryPrefix}${baseId}`;
  }

  /**
   * Creates URL-friendly slug from text
   * @param {string} text - Text to slugify
   * @returns {string} Slug
   */
  slugify(text) {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  /**
   * Calculates price range for grouped products
   * @param {Array} products - Product array
   * @returns {Object} Price range object
   */
  calculatePriceRange(products) {
    const prices = products.map((p) => p.price || 0).filter((p) => p > 0);

    if (prices.length === 0) {
      return { min: 0, max: 0 };
    }

    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
    };
  }

  /**
   * Calculates overall availability for grouped products
   * @param {Array} products - Product array
   * @returns {string} Availability status
   */
  calculateAvailability(products) {
    const availableCount = products.filter(
      (p) => p.availability === 'available'
    ).length;

    if (availableCount === products.length) {
      return 'available';
    } else if (availableCount > 0) {
      return 'partially-available';
    } else {
      return 'unavailable';
    }
  }

  /**
   * Consolidates tags from all products in group
   * @param {Array} products - Product array
   * @returns {Array} Consolidated tag array
   */
  consolidateTags(products) {
    const allTags = new Set();

    products.forEach((product) => {
      if (product.tags) {
        if (Array.isArray(product.tags)) {
          product.tags.forEach((tag) => allTags.add(tag));
        } else if (typeof product.tags === 'string') {
          product.tags.split(',').forEach((tag) => allTags.add(tag.trim()));
        }
      }
    });

    return Array.from(allTags);
  }

  /**
   * Extracts material information for smart sorting
   * @param {Array} products - Product array
   * @returns {Array} Material array
   */
  extractMaterials(products) {
    const materials = new Set();

    products.forEach((product) => {
      // Check various fields for material information
      const sources = [
        product.material,
        product.wood,
        product.title,
        product.tags?.join(' ') || '',
      ];

      sources.forEach((source) => {
        if (source && typeof source === 'string') {
          // Look for common wood/material names
          const materialMatches = source.match(
            /\b(cherry|oak|maple|pine|birch|walnut|mahogany|tfl|laminate|veneer)\b/gi
          );
          if (materialMatches) {
            materialMatches.forEach((match) =>
              materials.add(match.toLowerCase())
            );
          }
        }
      });
    });

    return Array.from(materials);
  }

  /**
   * Extracts finish information for smart sorting
   * @param {Array} products - Product array
   * @returns {Array} Finish array
   */
  extractFinishes(products) {
    const finishes = new Set();

    products.forEach((product) => {
      const sources = [
        product.finish,
        product.title,
        product.tags?.join(' ') || '',
      ];

      sources.forEach((source) => {
        if (source && typeof source === 'string') {
          // Look for common finish names
          const finishMatches = source.match(
            /\b(natural|stained|painted|clear|satin|gloss|matte)\b/gi
          );
          if (finishMatches) {
            finishMatches.forEach((match) => finishes.add(match.toLowerCase()));
          }
        }
      });
    });

    return Array.from(finishes);
  }

  /**
   * CRITICAL FIX: Extracts and merges ALL individual variants from all constituent products
   * This fixes the UX issue where CompositeProducts showed 3 separate products instead of unified variants
   * @param {Array} products - Array of products that make up the CompositeProduct
   * @returns {Array} All individual variants from all products, properly merged
   */
  extractAllVariants(products) {
    const allVariants = [];

    products.forEach((product, productIndex) => {
      // Detect variant source from multiple possible properties
      let variantsArray = null;

      if (product.variants && Array.isArray(product.variants)) {
        variantsArray = product.variants;
      } else if (
        product.product_variants &&
        Array.isArray(product.product_variants)
      ) {
        variantsArray = product.product_variants;
      } else if (
        product.available_variants &&
        Array.isArray(product.available_variants)
      ) {
        variantsArray = product.available_variants;
      } else if (product.options && Array.isArray(product.options)) {
        variantsArray = product.options;
      }

      if (variantsArray && variantsArray.length > 0) {
        // Product has multiple variants - add them all
        variantsArray.forEach((variant) => {
          const variantImage =
            variant.image ||
            variant.featured_image ||
            product.image ||
            product.featured_image;

          const enhancedVariant = {
            ...variant,
            // 🔥 FIX: Ensure image is inherited from product if variant doesn't have one
            image: variantImage,
            // Ensure we have source product info for traceability
            sourceProductId: product.id,
            sourceProductHandle: product.handle,
            sourceProductTitle: product.title,
            // Product-level metafield — must be stamped here because the composite
            // rebuild drops any field it doesn't explicitly carry, and a composite
            // merges inset + standard members so the parent can't answer this.
            sourceProductIsInset: product.is_inset === true,
            // Same reason for the 3D geometry: each member product (Paint Grade,
            // Veneers, White Oak...) carries its OWN render_pipeline.glb_file, so the
            // composite parent cannot answer "which GLB is this variant" either.
            sourceProductGlbUrl: product.glb_url || null,
            // Add composite context for debugging
            isFromCompositeProduct: true,
            compositeSourceIndex: productIndex,
          };
          allVariants.push(enhancedVariant);
        });
      } else {
        // Product is a single variant - create a variant object from the product itself
        const uniqueVariantId = `generated_${product.id}_${this.variantIdCounter++}`;
        const productImage = product.image || product.featured_image;

        const productAsVariant = {
          id: uniqueVariantId, // Use unique variant ID instead of product ID
          title: product.title,
          name: product.title,
          price: product.price,
          compareAtPrice: product.compareAtPrice,
          image: productImage,
          available: product.availability === 'available',
          // Source product info
          sourceProductId: product.id,
          sourceProductHandle: product.handle,
          sourceProductTitle: product.title,
          sourceProductIsInset: product.is_inset === true,
          sourceProductGlbUrl: product.glb_url || null,
          // Composite context
          isFromCompositeProduct: true,
          compositeSourceIndex: productIndex,
        };
        allVariants.push(productAsVariant);
      }
    });

    // Variant extraction complete for CompositeProduct
    return allVariants;
  }

  /**
   * Updates processing statistics for performance monitoring
   * @param {number} productCount - Number of products processed
   * @param {number} groupCount - Number of groups created
   * @param {number} processingTime - Processing time in milliseconds
   */
  updateProcessingStats(productCount, groupCount, processingTime) {
    this.processingStats.totalProcessed += productCount;
    this.processingStats.groupsCreated += groupCount;

    // Calculate rolling average processing time
    if (this.processingStats.averageProcessingTime === 0) {
      this.processingStats.averageProcessingTime = processingTime;
    } else {
      this.processingStats.averageProcessingTime =
        (this.processingStats.averageProcessingTime + processingTime) / 2;
    }
  }

  /**
   * Gets current processing statistics
   * @returns {Object} Processing stats
   */
  getProcessingStats() {
    return { ...this.processingStats };
  }

  /**
   * Resets processing statistics
   */
  resetProcessingStats() {
    this.processingStats = {
      totalProcessed: 0,
      groupsCreated: 0,
      averageProcessingTime: 0,
    };
    // Processing stats reset
  }

  /**
   * Marks bootstrap as complete - enables runtime protection
   * Called by data-pipeline after Layer 4 completes
   */
  markBootstrapComplete() {
    this.bootstrapComplete = true;
    isDev() &&
      console.log(
        '🎯 CompositeProductPreprocessor: Bootstrap complete - runtime protection enabled'
      );
  }

  // ============================================================================
  // EVENT PUBLISHING HELPER
  // ============================================================================

  /**
   * Helper method to publish events through CoreEventBus
   * Pattern from: md-config-bl-material-selector.js (Golden Standard)
   *
   * @param {string} eventType - Event type constant
   * @param {Object} eventData - Event payload
   */
  publishEvent(eventType, eventData) {
    if (window.MurphyDoorConfigurator?.CoreEventBus) {
      window.MurphyDoorConfigurator.CoreEventBus.publish(eventType, eventData);
    } else {
      isDev() &&
        console.warn(
          `⚠️ CoreEventBus not available, failed to publish: ${eventType}`
        );
    }
  }
}

// ============================================================================
// GLOBAL INITIALIZATION
// ============================================================================

// Initialize global instance
if (typeof window !== 'undefined') {
  if (!window.MurphyDoorConfigurator) {
    window.MurphyDoorConfigurator = {};
  }

  window.MurphyDoorConfigurator.compositeProductPreprocessor =
    new CompositeProductPreprocessor();
  // Dual-stage verification: execution log fires in process() method, load verification via initializer dependency checker
}

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CompositeProductPreprocessor;
}
